import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { getSkus } from "../../services/sku";
import { SKUS_CRUMBS } from "../../constants/crumbs";
import { SKUS_QUERY_STRING } from "../../constants/skus";
import { APP_PATHS } from "../../constants/paths";
import PortalBreadcrumb from "../UI/Breadcrumb/PortalBreadcrumb/PortalBreadcrumb";
import Subcategories from "./Subcategories/Subcategories";
import Loading from "../UI/Loading/Loading";
import EmptyContent from "../UI/EmptyContent/EmptyContent";
import SkusFilters from "./SkusFilters/SkusFilters";
import Pagination from "../Pagination/Pagination";
import SkuCard from "../SkuCard/SkuCard";
import classes from "./Skus.module.css";

const Skus = () => {
  const [skus, setSkus] = useState([]);
  const [skusTotalCount, setSkusTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageLimit] = useState(12);
  const [isSkusLoading, setIsSkusLoading] = useState(true);
  const [skusLoadingError, setSkusLoadingError] = useState("");
  const [searchParams] = useSearchParams();
  const categoryId = Number(searchParams.get(SKUS_QUERY_STRING.CATEGORY_ID));
  const subcategoryId = Number(
    searchParams.get(SKUS_QUERY_STRING.SUB_CATEGORY_ID)
  );
  const commercialName = searchParams
    .get(SKUS_QUERY_STRING.COMMERCIAL_NAME)
    ?.trim();
  const skuBrandsQueryString = searchParams
    .get(SKUS_QUERY_STRING.SKU_BRANDS)
    ?.trim();
  const carBrandsQueryString = searchParams
    .get(SKUS_QUERY_STRING.CAR_BRANDS)
    ?.trim();
  const carModelsQueryString = searchParams
    .get(SKUS_QUERY_STRING.CAR_MODELS)
    ?.trim();
  const carTransmissionsAndCCsQueryString = searchParams
    .get(SKUS_QUERY_STRING.CAR_TRANSMISSION_CC)
    ?.trim();
  const carYearListQueryString = searchParams
    .get(SKUS_QUERY_STRING.CAR_YEAR_LIST)
    ?.trim();
  const skuNamesQueryString = searchParams
    .get(SKUS_QUERY_STRING.SKU_NAMES)
    ?.trim();
  const skuIdsQueryString = searchParams.get(SKUS_QUERY_STRING.SKU_IDS)?.trim();

  const mainContentRef = useRef();

  const scrollToMainContentSection = () => {
    window.scrollTo({
      top: mainContentRef.current.offsetTop,
    });
  };

  const currentPageChangedHandler = (pageNumber) => {
    setPageNumber(pageNumber);
  };

  useEffect(() => {
    const fetchSkus = async () => {
      try {
        setIsSkusLoading(true);
        setSkusLoadingError("");
        const response = await getSkus(
          pageNumber,
          pageLimit,
          categoryId,
          subcategoryId,
          commercialName,
          skuBrandsQueryString,
          carBrandsQueryString,
          carModelsQueryString,
          carTransmissionsAndCCsQueryString,
          carYearListQueryString,
          skuNamesQueryString,
          skuIdsQueryString
        );
        const {
          data: { skusCount, skuList },
        } = response;
        setSkus(skuList);
        setSkusTotalCount(Number(skusCount));
      } catch (err) {
        setSkusLoadingError("لا يوجد منتجات لعرضها !");
      } finally {
        setIsSkusLoading(false);
      }
    };
    scrollToMainContentSection();
    fetchSkus();
  }, [
    pageNumber,
    pageLimit,
    categoryId,
    subcategoryId,
    commercialName,
    skuBrandsQueryString,
    carBrandsQueryString,
    carModelsQueryString,
    carTransmissionsAndCCsQueryString,
    carYearListQueryString,
    skuNamesQueryString,
    skuIdsQueryString,
  ]);

  return (
    <div className={classes["skus"]} ref={mainContentRef}>
      <PortalBreadcrumb
        paths={[
          ...SKUS_CRUMBS,
          {
            label:
              searchParams.get(SKUS_QUERY_STRING.CATEGORY_ID) &&
              searchParams.get(SKUS_QUERY_STRING.CATEGORY_NAME)
                ? searchParams.get(SKUS_QUERY_STRING.CATEGORY_NAME)
                : searchParams.get(SKUS_QUERY_STRING.COMMERCIAL_NAME)?.trim()
                ? searchParams.get(SKUS_QUERY_STRING.COMMERCIAL_NAME)
                : APP_PATHS.SKUS.label,
          },
        ]}
      />
      <section className={classes["skus__main-content"]}>
        <div>
          <Subcategories />
        </div>
        <section className={classes["skus-grid-container"]}>
          {commercialName && !isSkusLoading && (
            <div className={classes["skus-grid__search-text-container"]}>
              <p className={classes["skus-grid__search-text"]}>
                نتائج البحت "
                <span className={classes["skus-grid__search-text-result"]}>
                  {" "}
                  {commercialName}{" "}
                </span>
                "
              </p>
              <p className={classes["skus-grid__search-result-num"]}>
                ( <span className="numeric-content">{skusTotalCount} </span>
                نتائج )
              </p>
            </div>
          )}
          <SkusFilters />
          <div className={classes["skus-grid"]}>
            {isSkusLoading ? (
              <Loading className={classes["skus-grid__loading"]} />
            ) : skusLoadingError || skus.length === 0 ? (
              <EmptyContent
                message="لا يوجد منتجات لعرضها !"
                className={classes["skus-grid__empty"]}
              />
            ) : (
              <>
                {skus.map((sku, index) => (
                  <SkuCard
                    key={index}
                    id={sku.id}
                    imageUrl={sku.imageUrl}
                    brandName={sku.skuBrand}
                    name={sku.commercialName}
                    price={sku.suggestedSellingPrice}
                    isIncludedInBundles={sku.isIncludedInBundle}
                    availableQuantity={sku.quantity}
                    isCartRuleApplied={sku.isCartRuleApplied}
                  />
                ))}
                <Pagination
                  className={classes["skus-grid__pagination"]}
                  currentPage={pageNumber}
                  pageSize={pageLimit}
                  total={skusTotalCount}
                  onPageChange={currentPageChangedHandler}
                />
              </>
            )}
          </div>
        </section>
      </section>
    </div>
  );
};

export default Skus;
